/**
 * RTL (Right-to-Left) text alignment utilities
 */

// RTL languages that should be right-aligned
export const RTL_LANGUAGES = [
  "ar",
  "ar-SA",
  "ar-AE",
  "ar-BH",
  "ar-DZ",
  "ar-EG",
  "ar-IQ",
  "ar-JO",
  "ar-KW",
  "ar-LB",
  "ar-LY",
  "ar-MA",
  "ar-OM",
  "ar-QA",
  "ar-SD",
  "ar-SY",
  "ar-TN",
  "ar-YE",
  "he",
  "he-IL",
  "fa",
  "fa-IR",
  "ur",
  "ur-PK",
];

/**
 * Check if a locale is RTL
 */
export const isRTLLocale = (locale: string): boolean => {
  return RTL_LANGUAGES.includes(locale);
};

/**
 * Check if a locale is Arabic
 */
export const isArabicLocale = (locale: string): boolean => {
  return locale.startsWith("ar");
};

/**
 * Get text alignment class based on locale
 */
export const getTextAlignment = (locale: string, center = false): string => {
  if (center) return "text-center";
  return isRTLLocale(locale) ? "text-right" : "text-left";
};

/**
 * Get direction attribute value
 */
export const getTextDirection = (locale: string): "ltr" | "rtl" => {
  return isRTLLocale(locale) ? "rtl" : "ltr";
};

/**
 * Get flex alignment class based on locale
 */
export const getFlexAlignment = (locale: string, center = false): string => {
  if (center) return "justify-center items-center";
  return isRTLLocale(locale) ? "justify-end" : "justify-start";
};

/**
 * Get comprehensive RTL classes for an element
 */
export const getRTLClasses = (locale: string, additionalClasses = ""): string => {
  const isRTL = isRTLLocale(locale);
  const baseClasses = [isRTL ? "text-right" : "text-left", isRTL ? "rtl" : "ltr"];

  return `${baseClasses.join(" ")} ${additionalClasses}`.trim();
};

/**
 * Get CSS style object for RTL support
 */
export const getRTLStyle = (locale: string, centerAlign = false) => {
  const isRTL = isRTLLocale(locale);

  return {
    direction: isRTL ? ("rtl" as const) : ("ltr" as const),
    textAlign: centerAlign ? ("center" as const) : (isRTL ? ("right" as const) : ("left" as const)),
  };
};
